import { Button, Dropdown, Form, Menu, Modal, Select, Table } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination, { PaginationProps } from "antd/es/pagination";
import { LoadingOutlined } from "@ant-design/icons";
import { RootState } from "../../redux/rootReducer";
import {
  getUserSingup,
  resetSignupRequest,
  resetUsers,
  updateSingupRequest,
} from "../../redux/actions/userActions";
import { Actions, SignupStatus } from "../../shared/constant";
import tableUtils from "../../utils/tableUtils";
import { SignupReqColumn } from "../../shared/table/columns";
import icons from "../../shared/icons";
import SearchComponent from "../../components/_shared/SearchComponent";
import globalUtils from "../../utils/globalUtils";
import { getClientTypes } from "../../redux/actions/accountActions";
import SignupRequestForm from "../../components/_shared/SignupRequestForm";
import validateMessages from "../../utils/validate";
import { isClapAdminORClapManager } from "../../utils/clapUtils";

function SignupRequest() {
  const dispatch = useDispatch();
  const [signupForm] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [effect, setEffect] = useState(false);
  const {
    userDetails,
    singupReqCount,
    signupRequests,
    updateSignupRequestSuccess,
    updateSignupRequestPending,
    signupListPending,
  } = useSelector((state: RootState) => state.user);
  const { clientTypes } = useSelector((state: RootState) => state.account);

  useEffect(() => {
    dispatch(resetUsers());
    dispatch(getClientTypes());
  }, []);

  useEffect(() => {
    dispatch(
      getUserSingup({
        page,
        searchText,
        status,
        category,
      }),
    );
  }, [page, effect]);

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const onStatusChange = (value: string) => {
    if (value) setStatus(value);
    else setStatus("");
    setPage(1);
    setEffect(!effect);
  };

  const onCategoryChange = (value: string) => {
    if (value) setCategory(value);
    else setCategory("");
    setPage(1);
    setEffect(!effect);
  };

  const onCancel = () => {
    setVisible(false);
    setEditData(null);
  };

  useEffect(() => {
    if (updateSignupRequestSuccess) {
      dispatch(resetSignupRequest());
      onCancel();
      setEffect(!effect);
    }
  }, [updateSignupRequestSuccess]);

  const onOk = async () => {
    try {
      await signupForm.validateFields();
    } catch (errorInfo) {
      return;
    }

    const signupFields = signupForm.getFieldsValue(true);

    dispatch(
      updateSingupRequest({
        id: editData?.id,
        body: {
          category: [signupFields.category],
          full_name: signupFields.full_name,
          mobile_no: signupFields.mobile_no,
          ...(editData?.email !== signupFields.email && {
            email: signupFields.email,
          }),
          description: signupFields.description,
          total_songs: signupFields.total_songs,
          popular_songs: signupFields.popular_songs,
          status: signupFields.status,
        },
      }),
    );
  };

  const onEdit = (record: any) => {
    setEditData(record);
    signupForm.setFieldsValue({
      category: record.category[0],
      full_name: record.full_name,
      mobile_no: record.mobile_no,
      email: record.email,
      description: record.description,
      total_songs: record.total_songs,
      popular_songs: record.popular_songs,
      status: record.status,
    });
    setVisible(true);
  };

  const actions = {
    title: "",
    key: "action",
    align: "right",
    render: (_: any, record: any) =>
      isClapAdminORClapManager(userDetails) && (
        <div className="text-secondary">
          <Dropdown
            overlay={
              <Menu
                className="min-w-[100px] rounded-[3px]"
                items={[
                  {
                    label: (
                      <div onClick={() => onEdit(record)} aria-hidden="true">
                        Edit
                      </div>
                    ),
                    key: "EDIT",
                  },
                ]}
              />
            }
            trigger={["click"]}
          >
            <button type="button">{icons.DotsVerticle()}</button>
          </Dropdown>
        </div>
      ),
  };

  const afterClose = () => {
    signupForm.resetFields();
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div className="text-heading20">Signup Requests</div>
      </div>
      <div className="mt-5 mb-4 flex gap-x-4">
        <SearchComponent handleSearch={handleSearch} />
        <Select
          placeholder="Category"
          className="w-[220px]"
          allowClear
          showSearch
          filterOption={(inputValue, option) =>
            option?.props.children
              .toString()
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          }
          onChange={onCategoryChange}
        >
          {clientTypes.map((type: any) => (
            <Select.Option key={type.id} value={type.id}>
              {type.label}
            </Select.Option>
          ))}
        </Select>
        <Select
          placeholder="Status"
          className="w-[190px]"
          showSearch
          allowClear
          onChange={onStatusChange}
        >
          {Object.values(SignupStatus).map((item) => (
            <Select.Option key={item} value={item}>
              {globalUtils.snakeCaseToCapitalize(item)}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div>
        <Table
          rowKey={(record: any) => {
            return record.id;
          }}
          columns={tableUtils.buildColumns(
            signupRequests,
            SignupReqColumn,
            actions,
          )}
          dataSource={signupRequests}
          pagination={false}
          size="middle"
          scroll={{ x: 1024 }}
          loading={{
            spinning: signupListPending as boolean,
            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
          }}
        />
      </div>
      <div className="text-right mt-4">
        <Pagination
          current={page}
          onChange={handlePageChange}
          total={singupReqCount}
          showSizeChanger={false}
        />
      </div>

      <Modal
        visible={visible}
        title="Edit Signup Request"
        centered
        width={740}
        maskClosable={false}
        onOk={onOk}
        onCancel={onCancel}
        afterClose={afterClose}
        destroyOnClose
        footer={[
          <Button key="back" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={updateSignupRequestPending as boolean}
            onClick={onOk}
          >
            Save
          </Button>,
        ]}
      >
        <div className="mx-6 mt-6 mb-[26px]">
          <Form
            form={signupForm}
            layout="vertical"
            name="signupForm"
            validateMessages={validateMessages}
          >
            <SignupRequestForm mode={Actions.EDIT} />
          </Form>
        </div>
      </Modal>
    </div>
  );
}

export default SignupRequest;
